import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import Swal from 'sweetalert2'

import { TApi } from '../../../../../../shared/const'
import http from '../../../../../../shared/http'

import Btn from '../../../../../../UI/Btn'
import { PlainInput } from '../../../../../../UI/PlainField'

import SettingHint from '../SettingHint'

import shared from '../style.module.scss'
import styles from './style.module.scss'

/**
 * Pflege der Kategorien eines Unterstützungsbedarfs.
 *
 * Bis hierher gab es dafür keine Maske: Die Liste lag in einer Einstellung, die
 * sich in der Oberfläche nicht bearbeiten ließ — Kategorien entstanden also
 * faktisch gar nicht mehr, und „Jury" und „Jury-Einsatz" hätten nebeneinander
 * stehen können.
 *
 * Anders als die übrigen Stammdaten des Moduls liegt diese Liste nicht in einer
 * eigenen Tabelle, sondern in den Einstellungen (Option „supportCategories") —
 * am Bedarf steht der Name, nicht eine Nummer. Genau deshalb sperrt der Server
 * Umbenennen und Löschen, sobald ein Bedarf die Kategorie benutzt; die Maske
 * zeigt die Verwendungszahl, damit das vorher erkennbar ist.
 */
export default function SupportCategories() {
	const { t } = useTranslation()

	const [rows, setRows] = useState([])
	const [isLoading, setLoading] = useState(false)
	const [draft, setDraft] = useState('')
	const [editing, setEditing] = useState(null)

	const load = useCallback(() => {
		setLoading(true)
		http.get(TApi.ADMIN_SUPPORT_CATEGORIES_USAGE)
			.then(({ data }) => setRows(data))
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setLoading(false))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		load()
	}, [load])

	/** Speichert die gesamte Liste; der Server prüft, was noch in Verwendung ist. */
	const persist = names =>
		http
			.put(TApi.ADMIN_SUPPORT_CATEGORIES, { categories: names })
			.then(() => {
				setEditing(null)
				load()
			})
			.catch(() => {}) // Meldung kommt aus shared/http

	const names = rows.map(row => row.name)

	const add = () => {
		const name = draft.trim()
		if (name.length < 2) return
		if (names.includes(name)) {
			toast.warning('Diese Kategorie gibt es bereits.')
			return
		}
		persist([...names, name]).then(() => setDraft(''))
	}

	const rename = row => {
		const name = (editing?.value || '').trim()
		if (name.length < 2 || name === row.name) {
			setEditing(null)
			return
		}
		if (names.includes(name)) {
			toast.warning('Diese Kategorie gibt es bereits.')
			return
		}
		persist(names.map(current => (current === row.name ? name : current)))
	}

	const remove = async row => {
		if (row.usedBy > 0) {
			await Swal.fire({
				title: row.name,
				text: `Diese Kategorie ist ${row.usedBy}-mal an einem Unterstützungsbedarf vergeben und lässt sich deshalb weder entfernen noch umbenennen. Ändern Sie zuerst die Kategorie der betroffenen Bedarfe.`,
				icon: 'info',
				confirmButtonText: t('Ok') === 'Ok' ? 'Verstanden' : t('Ok'),
				confirmButtonColor: '#008b9c'
			})
			return
		}

		const result = await Swal.fire({
			title: row.name,
			text: 'Diese Kategorie ist an keinem Bedarf vergeben und wird aus der Auswahlliste entfernt.',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: t('delete'),
			cancelButtonText: t('Cancel'),
			confirmButtonColor: '#d33'
		})
		if (!result.isConfirmed) return

		persist(names.filter(current => current !== row.name))
	}

	// Kategorien, die nur noch an alten Bedarfen hängen: Sie stehen nicht in der
	// gepflegten Liste, sollen aber sichtbar sein — sonst ließe sich nie
	// nachvollziehen, woher eine im Bedarf angezeigte Kategorie kommt.
	const orphans = rows.filter(row => !row.listed)

	return (
		<div className={shared.root} id="supporter-support-categories">
			<section className={shared.section}>
				<h3 className={shared.sectionTitle}>Kategorien des Unterstützungsbedarfs</h3>
				<SettingHint example={`Beispiel: „Jury", „Workshop", „Coaching"`}>
					{`Auswahlliste im Feld „Kategorie", wenn Junior Achievement unter „Unterstützungsbedarf" einen Bedarf ausschreibt. Änderungen wirken sofort.`}
				</SettingHint>

				<div className={shared.sectionContent}>
					<div className={styles.group}>
						<div className={styles.items}>
							{rows
								.filter(row => row.listed)
								.map(row => (
									<span key={row.name} className={styles.item}>
										{editing?.name === row.name ? (
											<input
												autoFocus
												className={styles.itemName}
												value={editing.value}
												onChange={e =>
													setEditing({ ...editing, value: e.target.value })
												}
												onBlur={() => rename(row)}
												onKeyDown={e => {
													if (e.key === 'Enter') {
														e.preventDefault()
														rename(row)
													}
													if (e.key === 'Escape') setEditing(null)
												}}
											/>
										) : (
											<button
												type="button"
												className={styles.itemName}
												title={
													row.usedBy > 0
														? 'Vergeben — Umbenennen gesperrt'
														: 'Umbenennen'
												}
												disabled={row.usedBy > 0}
												onClick={() =>
													setEditing({ name: row.name, value: row.name })
												}
											>
												{row.name}
											</button>
										)}
										{row.usedBy > 0 && (
											<span
												className={styles.usage}
												title="Vergeben an Unterstützungsbedarfen"
											>
												{row.usedBy}×
											</span>
										)}
										<button
											type="button"
											className={styles.remove}
											onClick={() => remove(row)}
											title={t('delete')}
										>
											×
										</button>
									</span>
								))}
							{!rows.some(row => row.listed) && !isLoading && (
								<span className={styles.empty}>Noch keine Einträge.</span>
							)}
						</div>
					</div>

					{Boolean(orphans.length) && (
						<div className={styles.group}>
							<span className={styles.groupName}>Nur noch an bestehenden Bedarfen</span>
							<div className={styles.items}>
								{orphans.map(row => (
									<span
										key={row.name}
										className={clsx(styles.item, styles.itemInactive)}
									>
										<span className={styles.itemName}>{row.name}</span>
										<span className={styles.usage}>{row.usedBy}×</span>
										<button
											type="button"
											className={styles.remove}
											title="In die Auswahlliste übernehmen"
											onClick={() =>
												persist([
													...names.filter(n => n !== row.name),
													row.name
												])
											}
										>
											+
										</button>
									</span>
								))}
							</div>
						</div>
					)}
				</div>

				<div className={styles.addRow}>
					<PlainInput
						placeholder="Kategorie"
						value={draft}
						onChange={e => setDraft(e.target.value)}
						onKeyDown={e => {
							if (e.key === 'Enter') {
								e.preventDefault()
								add()
							}
						}}
					/>
					<Btn type="button" onClick={add}>
						Hinzufügen
					</Btn>
				</div>
			</section>
		</div>
	)
}
